/**
 * Standalone browser entry point for recapt-signal.js.
 *
 * Reads configuration from the <script> tag's data attributes,
 * creates a SignalPlugin, and starts an axos RecordEngine with
 * only the signal plugin attached (no recording sync, no inspect).
 *
 * Usage:
 *   <script
 *     src="https://cdn.recapt.app/browser/recapt-signal.js"
 *     data-public-key="pk_..."
 *     data-api-url="https://api.recapt.app/v1beta"
 *   ></script>
 */

import { RecordEngine } from "axos/record";
import { createSignalPlugin } from "./plugin";
import type { SignalPlugin } from "./plugin";

const DEFAULT_API_URL = "https://api.recapt.app/v1beta";

interface SignalInstance {
  engine: RecordEngine;
  plugin: SignalPlugin;
}

declare global {
  interface Window {
    RecaptSignal?: {
      instance: SignalInstance | null;
      start: (opts: {
        apiKey: string;
        apiUrl?: string;
        debug?: boolean;
      }) => SignalInstance;
      stop: () => void;
    };
  }
}

function findScriptTag(): HTMLScriptElement | null {
  const scripts = document.getElementsByTagName("script");
  return (
    Array.from(scripts).find(
      (s) => s.src.includes("recapt-signal") || s.src.includes("glimt-signal"),
    ) ?? null
  );
}

function start(opts: {
  apiKey: string;
  apiUrl?: string;
  debug?: boolean;
}): SignalInstance {
  const plugin = createSignalPlugin({
    apiUrl: opts.apiUrl ?? DEFAULT_API_URL,
    apiKey: opts.apiKey,
    debug: opts.debug ?? false,
  });

  const engine = new RecordEngine({
    plugins: [plugin],
    snapshotEveryMs: 30_000,
    debug: opts.debug ?? false,
  });

  return { engine, plugin };
}

function stop(): void {
  const recapt = window.RecaptSignal;
  if (!recapt?.instance) return;
  recapt.instance.engine.destroy();
  recapt.instance = null;
}

function initFromScriptTag(): void {
  const script = findScriptTag();
  if (!script) return;

  const apiKey = script.getAttribute("data-public-key");
  if (!apiKey) return;

  const apiUrl = script.getAttribute("data-api-url") ?? DEFAULT_API_URL;
  const debug = script.getAttribute("data-debug") === "true";

  const recapt = window.RecaptSignal;
  if (!recapt) return;

  recapt.instance = start({ apiKey, apiUrl, debug });

  if (debug) {
    console.debug("[signal] Auto-initialized from script tag");
  }
}

if (typeof window !== "undefined") {
  window.RecaptSignal = {
    instance: null,
    start,
    stop,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFromScriptTag);
  } else {
    requestAnimationFrame(() => initFromScriptTag());
  }
}
